import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Input,
  Breadcrumb,
  Modal,
  message,
  Button,
  DatePicker,
  Space,
  Grid,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Avatar,
  Tooltip,
  Dropdown,
  Tag,
  Select,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  DeleteOutlined,
  RightOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  SwapOutlined,
  UserOutlined,
  PhoneOutlined,
  DollarOutlined,
  CalendarOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { transactionsService, citiesService } from '../../common/services';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Can from '../../components/can';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Générer une couleur basée sur le nom
const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#722ed1', '#eb2f96'];
  const index = (name || '').charCodeAt(0) % colors.length;
  return colors[index];
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const local = phone.replace('+225', '').trim();
  return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

const formatDate = (date, dateStr) => {
  if (dateStr) return dateStr;
  if (!date) return '-';

  const formats = ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY'];
  let parsed = dayjs(date);

  if (!parsed.isValid()) {
    for (const fmt of formats) {
      parsed = dayjs(date, fmt);
      if (parsed.isValid()) break;
    }
  }

  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }

  return typeof date === 'string' ? date : '-';
};

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('fr-FR').format(amount);
};

const HistoriqueTransferts = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // Statistiques
  const [stats, setStats] = useState({
    totalTransferts: 0,
    montantTotal: 0,
    montantMoyen: 0,
    today: 0,
  });

  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [cityFilter, setCityFilter] = useState(undefined);
  const [cities, setCities] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-date');

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  // Calculer les stats
  // Filtres communs (liste + stats)
  const filterParams = useCallback(() => ({
    q: (q ? q.replace(/\s+/g, '') : '') || undefined,
    start: dateRange?.[0]?.format('YYYY-MM-DD'),
    end: dateRange?.[1]?.format('YYYY-MM-DD'),
    cityCode: cityFilter || undefined,
  }), [q, dateRange, cityFilter]);

  // Liste paginée (n'extrait QUE la page demandée — plus de fetch des 10000 lignes)
  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await transactionsService.transfertsHistoriqueList({ page, limit, sort, ...filterParams() });
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error("Erreur chargement transferts:", e);
      message.error("Erreur lors du chargement des transferts");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, filterParams]);

  // Stats via endpoint serveur agrégé (ne dépend pas de la pagination)
  const fetchStats = useCallback(async () => {
    try {
      const s = await transactionsService.transfertStats(filterParams());
      setStats({
        totalTransferts: s?.totalTransferts || 0,
        montantTotal: s?.montantTotal || 0,
        montantMoyen: s?.montantMoyen || 0,
        today: s?.today || 0,
      });
    } catch (e) {
      console.error("Erreur stats transferts:", e);
    }
  }, [filterParams]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Charger la liste des villes (pour le filtre)
  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        const list = Array.isArray(res)
          ? res
          : (res?.data || res?.items || res?.cities || []);
        setCities(list.filter(c => c?.isActive !== false));
      } catch (e) {
        console.error('Erreur chargement villes:', e);
      }
    })();
  }, []);

  const handleDelete = (row) => {
    Modal.confirm({
      title: (
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <span>Confirmer la suppression</span>
        </Space>
      ),
      content: (
        <Card size="small" style={{ marginTop: 16, backgroundColor: '#fff2f0' }}>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Text type="secondary">Expéditeur :</Text>
              <br />
              <Text strong>{row.senderName}</Text>
              <br />
              <Text style={{ fontSize: 12 }}>{formatPhoneNumber(row.senderPhone)}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Destinataire :</Text>
              <br />
              <Text strong>{row.receiverName}</Text>
              <br />
              <Text style={{ fontSize: 12 }}>{formatPhoneNumber(row.receiverPhone)}</Text>
            </Col>
            <Col span={24}>
              <Text type="secondary">Montant :</Text>
              <Tag color="blue" style={{ marginLeft: 8 }}>{formatAmount(row.amount)} F CFA</Tag>
            </Col>
          </Row>
        </Card>
      ),
      okText: "Oui, supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? '95%' : 480,
      onOk: async () => {
        try {
          await transactionsService.deleteHistoriqueTransfertById(row._id);
          message.success("Transfert supprimé avec succès !");
          fetchList();
        } catch (e) {
          console.error(e);
          message.error("Erreur lors de la suppression");
        }
      }
    });
  };

  // Export
  const fetchAllForExport = async () => {
    const cleanedQ = q ? q.replace(/\s+/g, '') : '';
    const params = {
      page: 1, limit: 5000, sort,
      q: cleanedQ || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end: dateRange?.[1]?.format('YYYY-MM-DD'),
      cityCode: cityFilter || undefined,
    };
    const data = await transactionsService.transfertsHistoriqueList(params);
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportExcel = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Expéditeur: it.senderName || '',
        'Tél. expéditeur': formatPhoneNumber(it.senderPhone || ''),
        'Ville expéditeur': it.senderCity?.nom || '',
        Destinataire: it.receiverName || '',
        'Tél. destinataire': formatPhoneNumber(it.receiverPhone || ''),
        'Ville destinataire': it.receiverCity?.nom || '',
        'Montant (F CFA)': it.amount ?? 0,
        Date: formatDate(it.date, it.dateStr),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Transferts');
      XLSX.writeFile(wb, 'transferts_uv.xlsx');
      message.success({ content: 'Export Excel réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export Excel: une erreur est survenue.', key: 'export' });
    }
  };

  const exportCSV = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Expediteur: it.senderName || '',
        Tel_Expediteur: formatPhoneNumber(it.senderPhone || ''),
        Ville_Expediteur: it.senderCity?.nom || '',
        Destinataire: it.receiverName || '',
        Tel_Destinataire: formatPhoneNumber(it.receiverPhone || ''),
        Ville_Destinataire: it.receiverCity?.nom || '',
        Montant: it.amount ?? 0,
        Date: formatDate(it.date, it.dateStr),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'transferts_uv.csv');
      message.success({ content: 'Export CSV réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export CSV: une erreur est survenue.', key: 'export' });
    }
  };

  const exportPDF = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const doc = new jsPDF();
      doc.text('Historique des Transferts UV', 14, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Expéditeur', 'Tél.', 'Ville', 'Destinataire', 'Tél.', 'Ville', 'Montant', 'Date']],
        body: all.map(it => [
          it.senderName || '',
          formatPhoneNumber(it.senderPhone || ''),
          it.senderCity?.nom || '',
          it.receiverName || '',
          formatPhoneNumber(it.receiverPhone || ''),
          it.receiverCity?.nom || '',
          `${formatAmount(it.amount)} F`,
          formatDate(it.date, it.dateStr)
        ]),
      });
      doc.save('transferts_uv.pdf');
      message.success({ content: 'Export PDF réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export PDF: une erreur est survenue.', key: 'export' });
    }
  };

  const exportMenuItems = [
    { key: 'excel', icon: <FileExcelOutlined style={{ color: '#52c41a' }} />, label: 'Excel (.xlsx)', onClick: exportExcel },
    { key: 'csv', icon: <DownloadOutlined style={{ color: '#1890ff' }} />, label: 'CSV', onClick: exportCSV },
    { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />, label: 'PDF', onClick: exportPDF },
  ];

  const columns = [
    {
      title: 'Expéditeur',
      key: 'sender',
      width: 220,
      render: (_, record) => {
        const name = record.senderName || 'Inconnu';
        const initials = name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase();
        const villeNom = record.senderCity?.nom;

        return (
          <Space align="start">
            <Avatar style={{ backgroundColor: getAvatarColor(name) }} size={36}>
              {initials}
            </Avatar>
            <div style={{ lineHeight: 1.3 }}>
              <Text strong style={{ display: 'block', fontSize: 13 }}>{name}</Text>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {formatPhoneNumber(record.senderPhone)}
              </Text>
              {villeNom && (
                <Tag icon={<EnvironmentOutlined />} color="geekblue" style={{ fontSize: 10, marginTop: 2 }}>
                  {villeNom}
                </Tag>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'arrow',
      width: 50,
      render: () => (
        <ArrowRightOutlined style={{ color: '#1890ff', fontSize: 16 }} />
      ),
    },
    {
      title: 'Destinataire',
      key: 'receiver',
      width: 220,
      render: (_, record) => {
        const name = record.receiverName || 'Inconnu';
        const initials = name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase();
        const villeNom = record.receiverCity?.nom;

        return (
          <Space align="start">
            <Avatar style={{ backgroundColor: getAvatarColor(name) }} size={36}>
              {initials}
            </Avatar>
            <div style={{ lineHeight: 1.3 }}>
              <Text strong style={{ display: 'block', fontSize: 13 }}>{name}</Text>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {formatPhoneNumber(record.receiverPhone)}
              </Text>
              {villeNom && (
                <Tag icon={<EnvironmentOutlined />} color="geekblue" style={{ fontSize: 10, marginTop: 2 }}>
                  {villeNom}
                </Tag>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      sorter: true,
      render: (amount) => (
        <Tag color="green" style={{ fontSize: 13, fontWeight: 600 }}>
          {formatAmount(amount)} F CFA
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 140,
      sorter: true,
      defaultSortOrder: 'descend',
      render: (d, row) => {
        const dateStr = formatDate(d, row.dateStr);
        const parts = dateStr.split(' ');

        return (
          <Tooltip title={dateStr}>
            <Space direction="vertical" size={0}>
              <Text style={{ fontSize: 12 }}>{parts[0] || dateStr}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{parts[1] || ''}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Can perm="transfers.history:delete">
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Can>
      ),
    },
  ];

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);
    if (sorter?.field || sorter?.columnKey) {
      const key = sorter.columnKey || sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      if (['date', 'amount', 'senderName', 'receiverName', 'senderPhone', 'receiverPhone'].includes(key)) {
        setSort(`${dir}${key}`);
      }
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: 'Transactions' },
          { title: 'Transferts UV' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total transferts</span>}
              value={stats.totalTransferts}
              valueStyle={{ color: '#fff', fontSize: isMobile ? 20 : 24 }}
              prefix={<SwapOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#f6ffed', borderColor: '#52c41a' }}
          >
            <Statistic
              title={<span style={{ color: '#52c41a' }}>Montant total</span>}
              value={stats.montantTotal}
              valueStyle={{ color: '#52c41a', fontSize: isMobile ? 18 : 22 }}
              suffix="F"
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#e6f7ff', borderColor: '#1890ff' }}
          >
            <Statistic
              title={<span style={{ color: '#1890ff' }}>Montant moyen</span>}
              value={stats.montantMoyen}
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? 18 : 22 }}
              suffix="F"
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#fff7e6', borderColor: '#fa8c16' }}
          >
            <Statistic
              title={<span style={{ color: '#fa8c16' }}>Aujourd'hui</span>}
              value={stats.today}
              valueStyle={{ color: '#fa8c16', fontSize: isMobile ? 20 : 24 }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et tableau */}
      <Card>
        {/* Filtres */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher par nom, téléphone..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              allowClear
              size={isMobile ? 'middle' : 'large'}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              onChange={(r) => { setDateRange(r); setPage(1); }}
              placeholder={['Date début', 'Date fin']}
              format="DD/MM/YYYY"
              allowClear
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              allowClear
              showSearch
              placeholder="Filtrer par ville"
              value={cityFilter}
              onChange={(v) => { setCityFilter(v); setPage(1); }}
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              suffixIcon={<EnvironmentOutlined />}
              optionFilterProp="label"
              options={cities.map(c => ({ value: c.code, label: c.name }))}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Dropdown
              menu={{ items: exportMenuItems }}
              trigger={['click']}
            >
              <Button
                icon={<DownloadOutlined />}
                size={isMobile ? 'middle' : 'large'}
                style={{ width: '100%' }}
              >
                Exporter <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
              </Button>
            </Dropdown>
          </Col>
        </Row>

        {/* Résultats */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            {total} transfert{total > 1 ? 's' : ''} trouvé{total > 1 ? 's' : ''}
          </Text>
        </div>

        {/* Tableau */}
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          onChange={onTableChange}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
          }}
          rowClassName={(_, index) =>
            index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
          }
        />
      </Card>

      {/* Styles */}
      <style>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-light:hover td,
        .table-row-dark:hover td {
          background-color: #e6f7ff !important;
        }
      `}</style>
    </>
  );
};

export default HistoriqueTransferts;
